import axios from "axios";
import React, { useState, useEffect, useCallback } from "react";
import NewModelModal from "./Modal";
import { useAuth } from "../services/auth.service";

export default function Ejecutivo() {
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [modalResult, setModalResult] = useState("");
  const [ejecutivos, setEjecutivos] = useState([]);
  const [currentFormAction, setCurrentFormAction] = useState("/");
  const [currentFormMethod, setCurrentFormMethod] = useState("POST");
  const [currentButtonText, setCurrentButtonText] = useState("Guardar");
  const [currentTitle, setCurrentTitle] = useState("Crear Ejecutivo");
  const [modalBody, setModalBody] = useState([]);
  const [viewsCatalog, setViewsCatalog] = useState([]);
  const { user } = useAuth();

  // Filters State
  const [filterName, setFilterName] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterSucursal, setFilterSucursal] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [sucursalOptions, setSucursalOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const defaultModalBody = [
    {
      id: "firstName",
      name: "firstName",
      type: "text",
      placeholder: "Nombre",
      value: "",
    },
    {
      id: "lastName",
      name: "lastName",
      type: "text",
      placeholder: "Apellido",
      value: "",
    },
    {
      id: "email",
      name: "email",
      type: "email",
      placeholder: "Correo",
      value: "",
    },
    {
      id: "password",
      name: "password",
      type: "password",
      placeholder: "Contraseña (opcional)",
      value: "",
    },
    {
      id: "phone",
      name: "phone",
      type: "text",
      placeholder: "Telefono",
      value: "",
    },
    {
      id: "sucursales",
      name: "sucursales",
      type: "select",
      dataSource: "/sedes",
      multiselect: true,
      value: "",
      defaultValue: [],
      limit: 3,
      placeholder: "Sucursales",
    },
    {
      id: "templates",
      name: "templates",
      type: "select",
      dataSource: "/template",
      displayColumn: "name",
      multiselect: true,
      value: "",
      defaultValue: [],
      limit: 10,
      placeholder: "Plantillas",
    },
    {
      id: "department",
      name: "department",
      type: "select",
      dataSource: "/department?columns=1",
      displayColumn: "name",
      multiselect: true,
      value: "",
      defaultValue: [],
      limit: 5,
      placeholder: "Departamento",
    },
    {
      id: "views",
      name: "views",
      type: "select",
      dataSource: "/views",
      multiselect: true,
      value: "",
      defaultValue: [],
      limit: 20,
      placeholder: "Accessible Views",
    },
    {
      id: "department_supervisor",
      name: "department_supervisor",
      type: "checkbox",
      value: false,
      placeholder: "Supervisor Departamento",
    },
  ];

  // Reusable fetch function with filters
  const fetchEjecutivos = useCallback(async (filters = {}) => {
    setLoading(true);
    const url = process.env.REACT_APP_BACKEND_API + "/ejecutivo";
    const params = {};
    if (filters.name?.trim()) params.name = filters.name.trim();
    if (filters.email?.trim()) params.email = filters.email.trim();
    if (filters.sucursal) params.sucursal = filters.sucursal;
    if (filters.department) params.department = filters.department;

    const config = {
      params,
      headers: {
        "Content-Type": "application/json",
        "x-access-token": user.accessToken,
      },
    };

    try {
      const result = await axios.get(url, config);
      setEjecutivos(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setEjecutivos([]);
      } else {
        console.error(error);
        setModalResult("Error cargando ejecutivos");
      }
    } finally {
      setLoading(false);
    }
  }, [user?.accessToken]);

  // Fetch initial ejecutivos
  useEffect(() => {
    if (user?.accessToken) {
      fetchEjecutivos();
    }
  }, [user?.accessToken, fetchEjecutivos]);

  // Fetch filter options (Sedes and Departments)
  useEffect(() => {
    async function fetchFilterOptions() {
      const config = {
        headers: {
          "Content-Type": "application/json",
          "x-access-token": user.accessToken,
        },
      };

      try {
        const sedesRes = await axios.get(process.env.REACT_APP_BACKEND_API + "/sedes", config);
        if (Array.isArray(sedesRes.data)) {
          const mapped = sedesRes.data.map((item) => {
            if (typeof item === "string") return { value: item, label: item };
            const val = item._id || item.value || item.name || "";
            const lbl = item.label || item.name || item._id || "";
            return { value: val, label: lbl };
          }).filter((item) => item.value);
          setSucursalOptions(mapped);
        }
      } catch (e) {
        console.error("Error loading sedes for filters:", e);
      }

      try {
        const deptRes = await axios.get(process.env.REACT_APP_BACKEND_API + "/department?columns=1", config);
        if (Array.isArray(deptRes.data)) {
          const mapped = deptRes.data.map((item) => {
            if (typeof item === "string") return { value: item, label: item };
            const val = item.name || item._id || item.value || "";
            const lbl = item.label || item.name || item._id || "";
            return { value: val, label: lbl };
          }).filter((item) => item.value);
          setDepartmentOptions(mapped);
        }
      } catch (e) {
        console.error("Error loading departments for filters:", e);
      }
    }

    if (user?.accessToken) {
      fetchFilterOptions();
    }
  }, [user?.accessToken]);

  // Fetch accessible views catalog
  useEffect(() => {
    async function fetchViewsCatalog() {
      const config = {
        headers: {
          "Content-Type": "application/json",
          "x-access-token": user.accessToken,
        },
      };
      const baseApi = `${process.env.REACT_APP_BACKEND_API || ""}`.replace(/\/+$/, "");
      const fallbackBase = baseApi.replace(/\/api$/, "");
      const urls = Array.from(new Set([`${baseApi}/views`, `${fallbackBase}/api/views`]));
      for (const url of urls) {
        const result = await axios.get(url, config).catch(() => null);
        if (Array.isArray(result?.data)) {
          const values = result.data
            .map((item) => item?._id)
            .filter(Boolean);
          setViewsCatalog(values);
          return;
        }
      }
      setViewsCatalog([]);
    }
    if (user?.accessToken) fetchViewsCatalog();
  }, [user?.accessToken]);

  const normalizeSelectValues = (input) => {
    if (!input) return [];
    if (Array.isArray(input)) {
      return input
        .map((item) => (typeof item === "string" ? item : item?.value))
        .filter(Boolean);
    }
    if (typeof input === "string") return [input];
    if (typeof input === "object" && input.value) return [input.value];
    return [];
  };

  const formOnChange = (e, action) => {
    try {
      const { name, value } = e.target;
      setModalBody(
        modalBody.map((item) => {
          if (item.type === "checkbox" && item.name === name) {
            item.value = e.target.checked;
          } else if (item.name === name) {
            item.value = value;
          }
          return item;
        })
      );
    } catch (error) {
      const items = e;
      setModalBody(
        modalBody.map((bodyItem) => {
          if (bodyItem.type === "select" && bodyItem.name === action.name) {
            bodyItem.value = items;
          }
          return bodyItem;
        })
      );
    }
  };

  const newEjecutivo = (e) => {
    e.preventDefault();
    setModalBody(
      defaultModalBody.map((item) => ({
        ...item,
        value: "",
        placeholder: item.id === "password" ? "Contraseña" : item.placeholder,
      }))
    );
    setCurrentFormAction("/");
    setCurrentFormMethod("POST");
    setCurrentButtonText("Guardar");
    setCurrentTitle("Crear Ejecutivo");
    openModal();
  };

  const deleteEjecutivo = (e) => {
    e.preventDefault();
    const id = ejecutivos[e.currentTarget.dataset.id]._id;
    fetch(process.env.REACT_APP_BACKEND_API + "/ejecutivo/" + id, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "x-access-token": user.accessToken,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setEjecutivos(ejecutivos.filter((ejecutivo) => ejecutivo._id !== id));
      })
      .catch((err) => console.error("Error deleting ejecutivo:", err));
  };

  const editEjecutivo = (e) => {
    e.preventDefault();
    const id = ejecutivos[e.currentTarget.dataset.id]._id;
    setCurrentFormAction("/" + id);
    setCurrentFormMethod("PUT");
    setCurrentButtonText("Actualizar");
    setCurrentTitle("Editar Ejecutivo");
    setModalBody(
      defaultModalBody.map((item) => {
        const clonedItem = { ...item };
        clonedItem.value =
          clonedItem.name === "password"
            ? ""
            : ejecutivos[e.currentTarget.dataset.id][clonedItem.name]
            ? ejecutivos[e.currentTarget.dataset.id][clonedItem.name]
            : "";
        if (clonedItem.name === "password") {
          clonedItem.placeholder = "Nueva contraseña (opcional)";
        }
        if (clonedItem.defaultValue) {
          clonedItem.defaultValue = ejecutivos[e.currentTarget.dataset.id][clonedItem.name]
            ? normalizeSelectValues(ejecutivos[e.currentTarget.dataset.id][clonedItem.name]).map(
                (optionItem) => {
                  return {
                    value: optionItem,
                    label: optionItem,
                    disabled: false,
                  };
                }
              )
            : [];
        }
        return clonedItem;
      })
    );
    openModal();
  };

  const cleanModalForm = () => {
    setModalBody(
      modalBody.map((item) => {
        item.value = "";
        return item;
      })
    );
  };

  const submitEjecutivo = (e) => {
    e.preventDefault();
    setModalSubmitting(true);
    const value = modalBody.reduce((acc, item) => {
      acc[item.name] = item.value;
      return acc;
    }, {});

    if (
      !`${value.firstName || ""}`.trim() ||
      !`${value.lastName || ""}`.trim() ||
      !`${value.email || ""}`.trim()
    ) {
      setModalResult("Nombre, apellido y correo son obligatorios");
      setModalSubmitting(false);
      return;
    }

    const normalizedViews = normalizeSelectValues(value.views);
    const invalidViews = normalizedViews.filter((view) => !viewsCatalog.includes(view));
    if (invalidViews.length > 0) {
      setModalResult(`Views invalidas: ${invalidViews.join(", ")}`);
      setModalSubmitting(false);
      return;
    }

    value.views = normalizedViews;
    if (!`${value.password || ""}`.trim()) {
      delete value.password;
    }

    fetch(process.env.REACT_APP_BACKEND_API + "/ejecutivo" + currentFormAction, {
      method: currentFormMethod,
      headers: {
        "Content-Type": "application/json",
        "x-access-token": user.accessToken,
      },
      body: JSON.stringify(value),
    })
      .then((res) => res.json())
      .then((data) => {
        if (currentFormMethod === "POST" && value.password) {
          axios({
            method: "post",
            headers: {
              "Content-Type": "application/json",
              "x-access-token": user.accessToken,
            },
            url: process.env.REACT_APP_BACKEND_API + "/auth/signup",
            data: {
              username: value.email,
              email: value.email,
              password: value.password,
              roles: ["user"],
            },
          })
            .then((res) => {
              setModalResult("Ejecutivo asignado correctamente");
            })
            .catch((err) => {
              console.error("Error signing up user:", err);
              setModalResult(err.message);
              setModalSubmitting(false);
            });
          setEjecutivos([...ejecutivos, data]);
          setModalResult("Ejecutivo creado correctamente");
        } else if (currentFormMethod === "PUT") {
          setEjecutivos(
            ejecutivos.map((ejecutivo) => {
              if (ejecutivo._id === data._id) {
                for (const key in data) {
                  if (data[key] !== "") {
                    ejecutivo[key] = data[key];
                  }
                }
                return ejecutivo;
              }
              return ejecutivo;
            })
          );
          setModalResult("Ejecutivo actualizado correctamente");
        }

        setTimeout(() => {
          setModalResult("");
        }, 3000);
        cleanModalForm();
        setModalSubmitting(false);
        closeModal();
      })
      .catch((err) => {
        console.error("Error submitting ejecutivo:", err);
        setModalResult(err.message);
        setModalSubmitting(false);
      });
  };

  const openModal = () => {
    const modal = document.querySelector(".main-modal");
    modal.classList.remove("fadeOut");
    modal.classList.add("fadeIn");
    modal.style.display = "flex";
  };

  const closeModal = () => {
    const modal = document.querySelector(".main-modal");
    modal.classList.remove("fadeIn");
    modal.classList.add("fadeOut");
    modal.style.display = "none";
    setModalBody(defaultModalBody);
  };

  // Filter application handlers
  const handleApplyFilters = (e) => {
    if (e) e.preventDefault();
    fetchEjecutivos({
      name: filterName,
      email: filterEmail,
      sucursal: filterSucursal,
      department: filterDepartment,
    });
  };

  const handleClearFilters = (e) => {
    if (e) e.preventDefault();
    setFilterName("");
    setFilterEmail("");
    setFilterSucursal("");
    setFilterDepartment("");
    fetchEjecutivos({});
  };

  // Modern Initials-based avatar generator
  const getAvatarInitials = (firstName = "", lastName = "") => {
    const f = firstName.charAt(0).toUpperCase();
    const l = lastName.charAt(0).toUpperCase();
    return `${f}${l}` || "?";
  };

  const getAvatarBg = (name = "") => {
    const colors = [
      "from-indigo-500 to-purple-600",
      "from-blue-500 to-indigo-600",
      "from-violet-500 to-purple-600",
      "from-pink-500 to-rose-600",
      "from-emerald-500 to-teal-600",
      "from-cyan-500 to-blue-600",
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-4 md:p-6 lg:p-8">
      {/* Container Card */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/50 p-6 md:p-8 transition-all duration-300">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between pb-8 border-b border-slate-100 gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Ejecutivos</h2>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700">
                {ejecutivos.length} Total
              </span>
            </div>
            <p className="text-slate-400 text-sm mt-1">
              Configure, gestione y asigne ejecutivos a las sedes correspondientes.
            </p>
          </div>
          <div>
            <button
              onClick={newEjecutivo}
              className="flex items-center justify-center gap-2 w-full md:w-auto px-5 py-3 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-semibold text-sm rounded-2xl shadow-lg shadow-indigo-100 hover:shadow-indigo-200 transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0 cursor-pointer"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
              </svg>
              Nuevo Ejecutivo
            </button>
          </div>
        </div>

        {/* Filter Controls Form */}
        <form
          onSubmit={handleApplyFilters}
          className="bg-slate-50/60 border border-slate-200/50 rounded-2xl p-5 mb-8 transition-all"
        >
          <div className="flex items-center justify-between mb-4 border-b border-slate-200/50 pb-2">
            <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
              Filtros de Búsqueda
            </span>
            {(filterName || filterEmail || filterSucursal || filterDepartment) && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="text-xs text-rose-500 hover:text-rose-600 font-semibold transition-colors cursor-pointer"
              >
                Limpiar Filtros
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Input Nombre */}
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wider">
                Nombre
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </span>
                <input
                  type="text"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                  placeholder="Buscar por nombre..."
                />
              </div>
            </div>

            {/* Input Correo */}
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wider">
                Correo
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </span>
                <input
                  type="text"
                  value={filterEmail}
                  onChange={(e) => setFilterEmail(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                  placeholder="Ej. correo@dominio.com"
                />
              </div>
            </div>

            {/* Select Sucursal */}
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wider">
                Sucursal
              </label>
              <select
                value={filterSucursal}
                onChange={(e) => setFilterSucursal(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all cursor-pointer"
              >
                <option value="">Todas las Sucursales</option>
                {sucursalOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Select Departamento */}
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wider">
                Departamento
              </label>
              <select
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all cursor-pointer"
              >
                <option value="">Todos los Departamentos</option>
                {departmentOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-4 pt-3 border-t border-slate-200/50">
            <button
              type="button"
              onClick={handleClearFilters}
              className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 bg-white hover:bg-slate-100 border border-slate-200 rounded-xl transition-all cursor-pointer"
            >
              Limpiar
            </button>
            <button
              type="submit"
              className="flex items-center gap-1.5 px-5 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-md shadow-indigo-100 hover:shadow-indigo-200 transition-all cursor-pointer"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              Filtrar
            </button>
          </div>
        </form>

        {/* Data Table / Content State */}
        {loading ? (
          <div className="py-20 flex flex-col items-center justify-center gap-3">
            <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span className="text-sm font-semibold text-slate-500">Cargando ejecutivos...</span>
          </div>
        ) : ejecutivos.length === 0 ? (
          <div className="py-20 text-center border border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
            <svg className="mx-auto h-12 w-12 text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-sm font-semibold text-slate-700">No se encontraron ejecutivos</h3>
            <p className="text-xs text-slate-400 mt-1">Pruebe ajustando los filtros de búsqueda o cree uno nuevo.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-slate-200/50 rounded-2xl">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50/70">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Ejecutivo
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Contacto
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Departamento
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Sede / Sucursal
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Creado
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {ejecutivos.map((ejecutivo, key) => {
                  const fullName = `${ejecutivo.firstName || ""} ${ejecutivo.lastName || ""}`.trim() || "Sin Nombre";
                  const initials = getAvatarInitials(ejecutivo.firstName, ejecutivo.lastName);
                  const avatarBg = getAvatarBg(fullName);

                  // Formatting the date
                  const dateVal = ejecutivo.fechaCreacion || ejecutivo.created_at || "";
                  const formattedDate = dateVal
                    ? new Date(dateVal).toLocaleDateString("es-ES", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })
                    : "-";

                  return (
                    <tr key={ejecutivo._id || key} className="hover:bg-slate-50/50 transition-colors duration-150">
                      {/* Name Card */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${avatarBg} text-white flex items-center justify-center font-bold text-sm shadow-sm transition-all`}>
                            {initials}
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-slate-800">{fullName}</div>
                            <div className="text-[10px] font-mono text-slate-400">ID: {ejecutivo._id ? `${ejecutivo._id.slice(-6)}` : "-"}</div>
                          </div>
                        </div>
                      </td>

                      {/* Contact details */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-700 flex items-center gap-1.5">
                          <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                          {ejecutivo.email || "-"}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-1">
                          <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                            />
                          </svg>
                          {ejecutivo.phone || ejecutivo.telefono || "-"}
                        </div>
                      </td>

                      {/* Department tags */}
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {ejecutivo.department && ejecutivo.department.length > 0 ? (
                            ejecutivo.department.map((dept, idx) => {
                              const label = dept.label || dept.value || (typeof dept === "string" ? dept : "");
                              return label ? (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-100"
                                >
                                  {label}
                                </span>
                              ) : null;
                            })
                          ) : (
                            <span className="text-xs text-slate-400 italic">Sin departamento</span>
                          )}
                        </div>
                      </td>

                      {/* Sede tags */}
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {ejecutivo.sucursales && ejecutivo.sucursales.length > 0 ? (
                            ejecutivo.sucursales.map((sucursal, idx) => {
                              const label = sucursal.label || sucursal.value || (typeof sucursal === "string" ? sucursal : "");
                              return label ? (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100"
                                >
                                  {label}
                                </span>
                              ) : null;
                            })
                          ) : (
                            <span className="text-xs text-slate-400 italic">Sin sucursal</span>
                          )}
                        </div>
                      </td>

                      {/* Created date */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        {formattedDate}
                      </td>

                      {/* Status indicator */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {!ejecutivo.status || ejecutivo.status === "Activo" || ejecutivo.estado === "Activo" ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                            Activo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-50 text-slate-600 border border-slate-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                            {ejecutivo.status || ejecutivo.estado || "Inactivo"}
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                        <div className="inline-flex justify-center gap-2">
                          <button
                            onClick={editEjecutivo}
                            data-id={key}
                            type="button"
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-xl border border-indigo-100 hover:border-indigo-200 transition-all cursor-pointer"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={deleteEjecutivo}
                            data-id={key}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-xl border border-rose-100 hover:border-rose-200 transition-all cursor-pointer"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <NewModelModal
        title={currentTitle}
        onClose={closeModal}
        body={modalBody}
        onSubmit={submitEjecutivo}
        modalResult={modalResult}
        onChange={formOnChange}
        method={currentFormMethod}
        action={currentFormAction}
        buttonText={currentButtonText}
      />
    </div>
  );
}
